// This program has been developed by students from the bachelor Computer Science
// at Utrecht University within the Software Project course. Copyright (c) Utrecht
// University (Department of Information and Computing Sciences)

import World from "@/utils/world/World";
import * as PIXI from "pixi.js";

import { levelData } from "@/utils/Types";
import FarmTheme from "@/utils/world/default/farm/Farm.theme";
import {
  get4WayBitmask,
  get8WayBitmask,
} from "@/utils/worldGeneration/default/Connectivity";
import {
  getDecorationSafeZone,
  makeBinary,
  mask,
} from "@/utils/worldGeneration/default/WorldGen";
import { valueNoise } from "@/utils/worldGeneration/default/Noise";
import Theme from "../Theme";
import { store } from "@/store";
import DesertTheme from "@/utils/world/default/desert/Desert.theme";

export default class FarmWorld extends World {
  theme: Theme;
  land: number[][];
  layout: levelData;
  buffer: number;
  app: PIXI.Application;
  canvas: HTMLCanvasElement;
  tileSize: number;
  pathMap: number[][];
  character: {
    sprite: PIXI.Sprite;
    textures: Record<string | number, PIXI.Texture<PIXI.Resource>>;
    direction: number;
  } | null = null;
  /**
   * @param land The map of where there's land and water
   * @param layout The level constraints
   * @param buffer The buffer zone around
   * @param app A reference to the pixi.js instance
   * @param canvas A reference to the canvas instance that pixi.js draws on
   * @param theme The theme of the world assets
   */
  constructor(
    land: number[][],
    layout: levelData,
    buffer: number,
    app: PIXI.Application,
    canvas: HTMLCanvasElement,
    theme: string
  ) {
    super();

    (this.land = land), (this.layout = layout);
    this.buffer = buffer;
    this.pathMap = this.getPathMap();
    this.app = app;
    this.canvas = canvas;
    this.tileSize =
      Math.min(
        this.canvas.width / this.land.length,
        this.canvas.height / this.land[0].length
      ) * 1.75;
    this.theme = this.selectTheme(theme);
    this.loadTheme();
  }

  //Render the worldstate 0
  public render(): void {
    this.renderGround();
    this.renderPaths();
    this.renderPathIcons();
    this.renderStartEndtiles();
    this.renderSmallDecorations(0.89);
    this.renderDecorations(0.93);
    this.renderCharacter();
  }
  //Reset the world back to it's starting position
  public reset(): void {
    if (this.character != null) {
      //Getting start position
      const startPos = {
        x: this.layout.start[0].x + this.buffer,
        y: this.layout.start[0].y + this.buffer,
      };
      //Setting sprite position
      const iso = this.getIsometricPosition(
        this.canvas,
        this.tileSize,
        startPos
      );
      this.character.sprite.x = iso.x;
      this.character.sprite.y = iso.y;
      //Setting sprite direction
      const dir = this.getTileDir(startPos)[0];
      this.character.direction = this.getRotationFromDirection(dir);
      this.character.sprite.texture = this.character.textures[dir];
    }
  }

  //#region RENDER GAME ELEMENTS

  //Render the ground tiles
  private renderGround() {
    //Load the assets
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["ground"].url
    );
    const textures = this.loadMappedSprite(
      this.theme.mappings.ground.width,
      this.theme.mappings.ground.height,
      this.theme.mappings.ground.mapping,
      baseTexture
    );

    //Find and render the correct sprite
    this.renderFromMap(this.land, textures, get8WayBitmask);
  }

  //Render the path tiles
  private renderPaths() {
    //Load the assets
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["path"].url
    );
    const textures = this.loadMappedSprite(
      this.theme.mappings.path.width,
      this.theme.mappings.path.height,
      this.theme.mappings.path.mapping,
      baseTexture
    );

    //Find and render the correct sprite
    this.renderFromMap(this.pathMap, textures, get4WayBitmask);
  }

  //Render the icons on the path
  private renderPathIcons() {
    //Load the assets
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["icon"].url
    );
    const textures = this.loadMappedSprite(
      this.theme.mappings.icon.width,
      this.theme.mappings.icon.height,
      this.theme.mappings.icon.mapping,
      baseTexture
    );

    const size = this.tileSize;
    const paths = this.layout.path;

    for (let i = 0; i < paths.length; i++) {
      const texture = textures[paths[i].type];
      if (!texture) continue;

      const sprite = PIXI.Sprite.from(texture);
      const isoTranslation = this.getIsometricPosition(this.canvas, size, {
        x: paths[i].x + this.buffer,
        y: paths[i].y + this.buffer,
      });

      //Assign position and size to sprite
      sprite.width = size;
      sprite.height = size;
      sprite.x = isoTranslation.x;
      sprite.y = isoTranslation.y;

      if (
        paths[i].x - 1 + this.buffer >= 0 &&
        paths[i].x + 1 + this.buffer < this.pathMap.length + 2 * this.buffer &&
        (this.pathMap[paths[i].x - 1 + this.buffer][paths[i].y + this.buffer] >
          0 ||
          this.pathMap[paths[i].x + 1 + this.buffer][paths[i].y + this.buffer] >
            0)
      ) {
        sprite.anchor.set(1, 0);
        sprite.scale.x *= -1;
      }

      this.app.stage.addChild(sprite);
    }
  }

  /**
   * Render the small decorations, such as grass, inside the safezone
   * @param threshold How likely a piece of decoration is to spawn
   */
  private renderSmallDecorations(threshold: number) {
    //Load the assets
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["small"].url
    );
    const textures = this.loadUnmappedSprites(
      this.theme.smallDecoSize.width,
      this.theme.smallDecoSize.height,
      baseTexture
    );
    const positions = this.getDecorationPositions(threshold);

    //Draw a random small decoration item
    for (let x = 0; x < this.land.length; x++) {
      for (let y = 0; y < this.land[0].length; y++) {
        if (positions[x][y] < threshold) continue;
        const mask = Math.floor(Math.random() * textures.length);
        const sprite = PIXI.Sprite.from(textures[mask]);
        const isoTranslation = this.getIsometricPosition(
          this.canvas,
          this.tileSize,
          {
            x: x,
            y: y,
          }
        );

        //Assign position and size to sprite
        sprite.width = this.tileSize;
        sprite.height = this.tileSize;
        sprite.x = isoTranslation.x;
        sprite.y = isoTranslation.y;
        this.app.stage.addChild(sprite);
      }
    }
  }
  /**
   * Render the decorations inside the safezone
   * @param threshold How likely a piece of decoration is to spawn
   */
  private renderDecorations(threshold: number) {
    //Load the assets
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["deco"].url
    );
    const textures = this.loadUnmappedSprites(
      this.theme.largeDecoSize.width,
      this.theme.largeDecoSize.height,
      baseTexture
    );
    const positions = this.getDecorationPositions(threshold);

    const w = this.tileSize;
    const h = 2 * this.tileSize;

    //Draw a random decoration item
    for (let x = 0; x < this.land.length; x++) {
      for (let y = 0; y < this.land.length; y++) {
        if (positions[x][y] == 1) {
          const mask = Math.floor(Math.random() * textures.length);
          const sprite = PIXI.Sprite.from(textures[mask]);
          const isoTranslation = this.getIsometricPosition(
            this.canvas,
            this.tileSize,
            {
              x: x,
              y: y,
            }
          );

          //Assign position and size to sprite
          sprite.width = w;
          sprite.height = h;
          sprite.anchor.set(0, 0.5);
          sprite.x = isoTranslation.x;
          sprite.y = isoTranslation.y;
          sprite.zIndex = 0;
          this.app.stage.addChild(sprite);
        }
      }
    }
  }

  //Render the start and end tile
  private renderStartEndtiles() {
    //Get the base textures of the start and end tile
    const baseTexStart = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["start"].url
    );
    const baseTexEnd = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["end"].url
    );

    //Get the textures of the start and end tile
    const startTextures = this.loadMappedSprite(
      this.theme.mappings.start.width,
      this.theme.mappings.start.height,
      this.theme.mappings.start.mapping,
      baseTexStart
    );
    const endTextures = this.loadMappedSprite(
      this.theme.mappings.end.width,
      this.theme.mappings.end.height,
      this.theme.mappings.end.mapping,
      baseTexEnd
    );

    //Calculate the start and end tile direction based on the paths
    const startPos = {
      x: this.layout.start[0].x + this.buffer,
      y: this.layout.start[0].y + this.buffer,
    };
    const endPos = {
      x: this.layout.end[0].x + this.buffer,
      y: this.layout.end[0].y + this.buffer,
    };
    const startDir = this.getTileDir(startPos);
    const endDir = this.getTileDir(endPos);

    //Get the sprites with the right properties
    const startSprite = this.getAssetSprite(startTextures[startDir], startPos);
    const endSprite = this.getAssetSprite(endTextures[endDir], endPos);

    //Add the sprites to the application
    this.app.stage.addChild(startSprite);
    this.app.stage.addChild(endSprite);
  }

  //Render the character on the starttile
  private renderCharacter() {
    //Load character, texture
    const baseTexture = PIXI.BaseTexture.from(
      PIXI.Loader.shared.resources["char"].url
    );
    const textures = this.loadMappedSprite(
      this.theme.mappings.character.width,
      this.theme.mappings.character.height,
      this.theme.mappings.character.mapping,
      baseTexture
    );

    //Get the start direction of the player
    const startPos = {
      x: this.layout.start[0].x + this.buffer,
      y: this.layout.start[0].y + this.buffer,
    };
    const direction = this.layout.start[0].rot;

    //Set the correct sprite
    const dir = this.getDirFromRotation(direction);
    const sprite = this.getAssetSprite(textures[dir], startPos);

    //Make reference for sprite and add to the world
    this.character = {
      sprite: sprite,
      textures: textures,
      direction: direction,
    };

    this.app.stage.addChild(sprite);
  }

  //#endregion

  //#region HELPER FUNCTIONS

  /**
   * Renders assets from a map
   * @param map the map to render from
   * @param textures a dictionary with the possible textures to render in the map
   */
  private renderFromMap(
    map: number[][],
    textures: Record<string | number, PIXI.Texture<PIXI.Resource>>,
    getBitMask: (x: number, y: number, terrain: number[][]) => number
  ) {
    const size = this.tileSize;

    for (let x = 0; x < map.length; x++) {
      for (let y = 0; y < map[0].length; y++) {
        if (map[x][y] == 0) continue;
        const mask = getBitMask(x, y, map);
        const texture = textures[mask];
        if (!texture) {
          const g: PIXI.Graphics = new PIXI.Graphics();
          g.beginFill(0xff00ff);
          g.drawRect(
            (x - y) * size * 0.5,
            x * size * 0.25 + y * size * 0.25,
            size,
            size
          ),
            this.app.stage.addChild(g);

          continue;
        }
        const sprite = PIXI.Sprite.from(texture);
        const isoTranslation = this.getIsometricPosition(this.canvas, size, {
          x: x,
          y: y,
        });

        //Assign position and size to sprite
        sprite.width = size;
        sprite.height = size;
        sprite.x = isoTranslation.x;
        sprite.y = isoTranslation.y;
        this.app.stage.addChild(sprite);
      }
    }
  }
  /**
   * Create a map of the path locations from the path constraints
   * @returns a map of the path where the outer array is the x-axis and the inner array the y-axis
   */
  private getPathMap(): number[][] {
    //Create empty array and fill it with only zero's
    const res: number[][] = [];
    for (let x = 0; x < this.land.length; x++) {
      res.push(Array<number>(this.land[0].length).fill(0));
    }

    //Fill the array with ones where the path is and return it
    for (let i = 0; i < this.layout.path.length; i++) {
      res[this.layout.path[i].x + this.buffer][
        this.layout.path[i].y + this.buffer
      ] = 1;
    }
    return res;
  }
  /**
   * Makes a sprite for the start or end tile with the right properties
   * @param tex the texture of the sprite
   * @param xPos the position (on the x-axis) of the sprite
   * @param yPos the position (on the y-axis) of the sprite
   */
  private getAssetSprite(
    tex: PIXI.Texture<PIXI.Resource>,
    pos: { x: number; y: number }
  ): PIXI.Sprite {
    const isoTranslation = this.getIsometricPosition(
      this.canvas,
      this.tileSize,
      pos
    );

    const sprite = PIXI.Sprite.from(tex);
    sprite.width = this.tileSize;
    sprite.height = 2 * this.tileSize;
    sprite.anchor.set(0, 0.5);
    sprite.x = isoTranslation.x;
    sprite.y = isoTranslation.y;

    return sprite;
  }
  /**
   * Gets the direction(s) in which the character can walk from a specific tile.
   * This is used for getting the direction of assets on the start and finish tile.
   * @param x position (on the x-axis) of the tile
   * @param y position (on the y-axis) of the tile
   */
  private getTileDir(pos: { x: number; y: number }): string {
    let directions = "";

    const onTheMap =
      pos.x - 1 > 0 &&
      pos.x + 1 < this.pathMap.length &&
      pos.y - 1 > 0 &&
      pos.y + 1 < this.pathMap[0].length;

    if (onTheMap) {
      if (this.pathMap[pos.x][pos.y - 1]) directions += "n";
      if (this.pathMap[pos.x + 1][pos.y]) directions += "e";
      if (this.pathMap[pos.x][pos.y + 1]) directions += "s";
      if (this.pathMap[pos.x - 1][pos.y]) directions += "w";
    }

    if (directions.length <= 0) return "nesw";
    return directions;
  }
  /**
   * Get the direction in which the character should go with a specific rotation.
   * @returns Direction
   */
  public getDirFromRotation(rad: number): string {
    switch (rad) {
      case 0:
        return "n";
      case 90:
        return "e";
      case 180:
        return "s";
      case 270:
        return "w";
    }
    return "e";
  }
  /**
   * Gets the correct direction in radius
   * @param direction north, east, south or west
   * @returns returns the radius of the direction north = 0 and south = pi
   */
  private getRotationFromDirection(direction: string): number {
    switch (direction) {
      case "n":
        return 0;
      case "e":
        return 90;
      case "s":
        return 180;
      case "w":
        return 270;
    }
    return 0;
  }
  /**
   * Gets the positions where decorations can be placed
   * @param threshold how likely a piece of decoration is to spawn
   * @returns a map with positions
   */
  private getDecorationPositions(threshold: number): number[][] {
    const safeZone = getDecorationSafeZone(this.land, this.layout, this.buffer);
    const noise = makeBinary(
      valueNoise(
        this.layout.seed.toString(),
        this.land.length,
        this.land[0].length
      ),
      threshold
    );
    return mask(noise, safeZone);
  }

  //#endregion

  //#region THEMA FUNCTIONS

  /**
   * Selects the chosen world theme
   * @param theme Selected theme
   */
  selectTheme(theme: string): Theme {
    switch (theme) {
      case "farm": {
        store.commit("setTheme", "farm");
        return new FarmTheme();
      }
      case "desert": {
        store.commit("setTheme", "desert");
        return new DesertTheme();
      }
      default:
        store.commit("setTheme", "farm");
        return new FarmTheme();
    }
  }
  //Loads the chosen theme, by calling the belonging render method
  loadTheme(): void {
    PIXI.Loader.shared.reset();
    PIXI.Loader.shared
      .add(this.theme.assets)
      .load()
      .onComplete.once(() => this.render());
  }
  //#endregion
}
